/**
 * Where the two tokens live — adapted from apps/gms/src/lib/auth/token-store.ts.
 *
 * ACCESS TOKEN — memory only (15-minute bearer; a reload mints a new one).
 * REFRESH TOKEN — localStorage under an MSES-only key, so signing in or out of
 * GMS or OVR in another tab on the same machine never disturbs the medal desk
 * (which signs in as MEDAL_DESK, a role those apps never use).
 *
 * Nothing here decides WHEN to clear tokens. That is `isCredentialVerdict()`
 * from @seagames/contracts, applied in one place (`api/client.ts`).
 */

export const REFRESH_KEY = "seagames.mses.refresh_token";

let accessToken: string | null = null;

function safeRead(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function safeWrite(key: string, value: string | null): void {
	try {
		if (value === null) localStorage.removeItem(key);
		else localStorage.setItem(key, value);
	} catch {
		/* memory-only session; acceptable degradation */
	}
}

export const getAccessToken = (): string | null => accessToken;
export const getRefreshToken = (): string | null => safeRead(REFRESH_KEY);

/** True when a session might exist — only the server can rule on it. */
export const hasStoredSession = (): boolean => getRefreshToken() !== null;

export function setTokens(
	next: { accessToken: string; refreshToken?: string | undefined },
	/**
	 * Compare-and-swap: persist the rotation only if the token we rotated FROM
	 * is still the stored one, so a tab that slept mid-refresh cannot write a
	 * superseded token over a newer rotation (→ REFRESH_REUSE, session killed).
	 */
	expect?: { refreshToken: string },
): void {
	accessToken = next.accessToken;
	// Grace-window refreshes omit `refresh_token`: keep the one we have.
	if (next.refreshToken === undefined) return;
	if (expect && safeRead(REFRESH_KEY) !== expect.refreshToken) return;
	safeWrite(REFRESH_KEY, next.refreshToken);
}

export type SignOutReason = "revoked" | "expired" | "user";
type Listener = (reason: SignOutReason) => void;
const listeners = new Set<Listener>();

/** Fired only when the SERVER has ruled on the credential, or the user asked. */
export function onSignOut(fn: Listener): () => void {
	listeners.add(fn);
	return () => listeners.delete(fn);
}

export function clearTokens(reason: SignOutReason = "user"): void {
	accessToken = null;
	safeWrite(REFRESH_KEY, null);
	for (const fn of listeners) fn(reason);
}
